import type { TareData } from "./TareData";

import { getAccountStatus, getAccountStatusSync } from "./getAccountStatus";
import { getAppModel, getAppModelSync } from "./getAppModel";
import { getAppSessionInfo, getAppSessionInfoSync } from "./getAppSessionInfo";
import { getDeviceModel, getDeviceModelSync } from "./getDeviceModel";
import { getTareBridge } from "./getTareBridge";
import { setCachedTareData } from "./TareDataCache";
import { getDeviceProperties } from "../../logging/LoggableDeviceProperties";
import { sanitizeError } from "../../logging/RenderErrorBoundary";
import { getRnErrorProviderId } from "../../util/getRnErrorProviderId";

import {
  AvroOptional,
  log as signalsLogRenderError,
} from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNRenderErrorV4";
import { createContext, useContext } from "react";

// Re-export for backward compatibility
export { getCachedTareData } from "./TareDataCache";
export type { TareData } from "./TareData";

// Helper function to check if the native bridge is ready
async function waitForBridgeReady(maxWaitMs: number = 2000): Promise<boolean> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    const bridge = getTareBridge();
    if (bridge && bridge.getAppModel && bridge.getAccountStatus && bridge.getDeviceModel) {
      // Try a quick test call to see if the bridge is truly ready
      try {
        const testPromise = Promise.race([
          bridge.getAppModel(),
          new Promise((resolve) => setTimeout(() => resolve(null), 100)),
        ]);

        const result = await testPromise;
        if (result !== null) {
          return true; // Bridge is ready and responding
        }
      } catch (error) {
        const { message, name, stack } = sanitizeError(error);
        signalsLogRenderError({
          message: `TARE bridge initialization caught an error: ${message}`,
          name: name || "TareBridgeNotReady",
          stack: stack || "",
          componentStack: "",
          deviceProperties: getDeviceProperties(),
          provider: AvroOptional.string("unknown"),
          providerId: { ProviderId: getRnErrorProviderId("unknown") },
        });
      }
    }

    // Wait a bit before checking again
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return false; // Timeout waiting for bridge to be ready
}

export function getTareDataSync(): TareData {
  const appModel = getAppModelSync();
  const accountStatus = getAccountStatusSync();
  const deviceModel = getDeviceModelSync();
  const appSessionInfo = getAppSessionInfoSync();
  return {
    appModel,
    accountStatus,
    deviceModel,
    appSessionInfo,
  };
}

export async function getTareData(): Promise<TareData> {
  // Wait for bridge to be ready before making any calls
  const bridgeReady = await waitForBridgeReady();

  if (!bridgeReady) {
    // this will be sent to datadog log
    console.error("TARE bridge not ready after 2 seconds");
    signalsLogRenderError({
      message: "TARE bridge not ready after 2 seconds",
      name: "TareBridgeNotReady",
      stack: new Error().stack || "",
      componentStack: "",
      deviceProperties: getDeviceProperties(),
      provider: AvroOptional.string("unknown"),
      providerId: { ProviderId: getRnErrorProviderId("unknown") },
    });
  }

  const [appModel, accountStatus, deviceModel, appSessionInfo] = await Promise.all([
    getAppModel(),
    getAccountStatus(),
    getDeviceModel(),
    getAppSessionInfo(),
  ]);

  const tareData: TareData = {
    appModel,
    accountStatus,
    deviceModel,
    appSessionInfo,
  };

  setCachedTareData(tareData);

  return tareData;
}

const TareDataContext = createContext<TareData | null>(null);

export function useTareData() {
  const context = useContext(TareDataContext);
  if (!context) {
    throw new Error("TareDataContext used without initialization");
  }
  return context;
}

export function TareDataProvider({
  tareData,
  children,
}: {
  tareData: TareData;
  children: React.ReactNode;
}) {
  return <TareDataContext.Provider value={tareData}>{children}</TareDataContext.Provider>;
}
